/**
 * ChatWindow.tsx
 * 
 * Facebook-style chat window component that:
 * - Floats in bottom-right corner
 * - Can be minimized to just header
 * - Can be expanded to full page
 * - Supports multiple chatbots
 * - Persists across page navigation
 */

import React, { useState, useRef, useEffect } from 'react'
import { X, Maximize2, MessageCircle, Minus, Paperclip } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'

interface Chatbot {
  id: string
  name: string
  image: string
  description: string
  isFree: boolean
}

interface ChatWindowProps {
  chatbot: Chatbot
  isOpen: boolean
  isMinimized: boolean
  isExpanded: boolean
  onClose: () => void
  onMinimize: () => void
  onExpand: () => void
  onRestore: () => void
  zIndex: number
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  chatbot,
  isOpen,
  isMinimized,
  isExpanded,
  onClose,
  onMinimize,
  onExpand,
  onRestore,
  zIndex
}) => {
  const [messages, setMessages] = useState<any[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { user, token } = useAuth()

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Mock initial message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: 1,
          text: `Hi! I'm ${chatbot.name}. How can I help you today?`,
          sender: 'bot',
          timestamp: new Date()
        }
      ])
    }
  }, [isOpen, chatbot.name])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setSelectedFiles(prev => [...prev, ...files])
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && selectedFiles.length === 0) return

    const userMessage = {
      id: Date.now(),
      text: inputMessage || (selectedFiles.length > 0 ? 'Files uploaded' : ''),
      sender: 'user' as const,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setSelectedFiles([])
    setIsTyping(true)

    try {
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Get user's org ID
      const orgId = user?.org_id
      if (!orgId) {
        throw new Error('User organization ID not found')
      }

      // Get client_url from URL parameters if available
      const urlParams = new URLSearchParams(window.location.search)
      const clientUrl = urlParams.get('client_url')
      
      // Create form data
      const formData = new FormData()
      // Send a default message if only files are being sent
      const messageToSend = inputMessage.trim() || (selectedFiles.length > 0 ? 'Files uploaded' : '')
      formData.append('message', messageToSend)
      if (clientUrl) {
        formData.append('client_url', clientUrl)
      }
      
      // Add files to form data
      selectedFiles.forEach((file) => {
        formData.append('files', file)
      })
      
      const response = await fetch(`http://localhost:8000/api/v1/orgs/${orgId}/bots/${chatbot.id}/chat/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        // Don't set Content-Type - let browser set it for FormData
        body: formData
      })

      if (response.ok) {
        // Remove typing indicator when streaming starts
        setIsTyping(false)
        // Handle streaming response
        const reader = response.body?.getReader()
        const decoder = new TextDecoder()

        if (!reader) {
          throw new Error('No response body reader available')
        }

        let streamingContent = ''
        const botResponse = {
          id: Date.now() + 1,
          text: '',
          sender: 'bot' as const,
          timestamp: new Date()
        }
        
        // Add the bot response immediately to show streaming
        setMessages(prev => [...prev, botResponse])

        while (true) {
          const { done, value } = await reader.read()
          
          if (done) break

          const chunk = decoder.decode(value)
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6))
                
                if (data.status === 'processing') {
                  // Update the bot response with processing status
                  setMessages(prev => prev.map(msg => 
                    msg.id === botResponse.id 
                      ? { ...msg, text: `🔄 ${data.message}` }
                      : msg
                  ))
                } else if (data.status === 'generating') {
                  // Clear processing status and start content
                  setMessages(prev => prev.map(msg => 
                    msg.id === botResponse.id 
                      ? { ...msg, text: '' }
                      : msg
                  ))
                } else if (data.status === 'streaming') {
                  // Append streaming content
                  streamingContent += data.content
                  setMessages(prev => prev.map(msg => 
                    msg.id === botResponse.id 
                      ? { ...msg, text: streamingContent }
                      : msg
                  ))
                } else if (data.status === 'complete') {
                  // Finalize the message
                  setMessages(prev => prev.map(msg => 
                    msg.id === botResponse.id 
                      ? { ...msg, text: streamingContent || 'No response content received.' }
                      : msg
                  ))
                } else if (data.status === 'error') {
                  // Handle error
                  setMessages(prev => prev.map(msg => 
                    msg.id === botResponse.id 
                      ? { ...msg, text: `Error: ${data.message}` }
                      : msg
                  ))
                }
              } catch (parseError) {
                console.error('Error parsing streaming data:', parseError)
              }
            }
          }
        }
      } else {
        // Handle error response
        const botResponse = {
          id: Date.now() + 1,
          text: 'Sorry, I encountered an error. Please try again.',
          sender: 'bot' as const,
          timestamp: new Date()
        }
        setMessages(prev => [...prev, botResponse])
      }
    } catch (error) {
      console.error('Error sending message:', error)
      // Fallback response on error
      const botResponse = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot' as const,
        timestamp: new Date()
      }
      setMessages(prev => [...prev, botResponse])
    } finally {
      setIsTyping(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  if (!isOpen) return null

  if (isMinimized) {
    return null // Don't show individual minimized headers anymore
  }

  return (
    <div 
      className={`fixed bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col ${
        isExpanded 
          ? 'top-4 left-4 right-4 bottom-4' 
          : 'bottom-4 right-4 w-80 h-96'
      }`}
      style={{ zIndex }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img src={chatbot.image} alt={chatbot.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-900">{chatbot.name}</h3>
            <p className="text-xs text-gray-500">Online</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={onMinimize}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="Minimize"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            onClick={isExpanded ? onRestore : onExpand}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title={isExpanded ? "Restore" : "Expand"}
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${
                message.sender === 'user'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <p className="text-sm">{message.text}</p>
              <p className="text-xs opacity-70 mt-1">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-900 px-3 py-2 rounded-lg">
              <p className="text-sm">Typing...</p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className="mb-3 space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                <span className="text-sm text-gray-600 truncate">{file.name}</span>
                <button
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex space-x-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-2 text-gray-500 hover:text-gray-700 transition-colors"
            title="Attach file"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept=".txt,.pdf,.docx,.md"
          />
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() && selectedFiles.length === 0}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChatWindow 